import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal, ModalBase } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { Template } from 'packages/store/templates/types';
import TemplateContextMenu from 'packages/pages/components/templates/TemplateContextMenu';
import useAccessRights from 'packages/hooks/useAccessRights';
import useQueryParams from 'packages/hooks/useQueryParams';
import { type HiddenActions } from 'packages/pages/components/templates/TemplateContextMenu/types';
import { ActionStatus, Entity, TemplateAction } from 'packages/hooks/useAccessRights/types';
import modalContainer from 'packages/common/modalContainer';
import { templatesSelector } from '~/store/templates/templates.selectors';
import { changePage } from '~/store/general/general.actions';
import roles from '~/configs/roles';
import Preview from '../designCreateModal/pages/preview/Preview';
import { useDeferredClose } from '~/hooks/useDeferredClose';

type Params = {
    setDeleteModalTemplate: (templates: Template[]) => void;
    setRenameModalTemplate: (template: Template) => void;
    setMoveModalTemplate?: (templates: Template[]) => void;
    setShareModalTemplate?: (template: Template) => void;
    createDesign: (template: Template) => void;
    duplicateTemplate: (template: Template) => void;
};

const StyledModalContent = styled.div`
    grid-area: content;
`;

export const TemplateModalContent = ({
    template,
    backButton,
    createDesign,
    duplicateTemplate,
    setDeleteModalTemplate,
    setMoveModalTemplate,
    setRenameModalTemplate,
    setShareModalTemplate,
}: {
    template?: Template | null;
    createDesign?: () => void;
    backButton?: {
        title: string;
        onClick: () => void;
    };
} & Params) => {
    const { translate } = useTranslate();
    const { isEntityActionAllowed, isAccountRoleAssigned } = useAccessRights();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { resetQueryParams } = useQueryParams();

    const hiddenMenuActions: HiddenActions = [
        'showPreview',
        'editTemplate',
        'removeFromCategory',
        'setMoveModalTemplate',
    ];

    // if (!template?.categoryId) {
    //     hiddenMenuActions.push('removeFromCategory');
    // }

    // if (!setMoveModalTemplate) {
    //     hiddenMenuActions.push('removeFromCategory');
    //     hiddenMenuActions.push('setMoveModalTemplate');
    // }

    const editTemplate = useCallback(() => {
        if (!template) {
            return;
        }

        dispatch(
            changePage({
                back: location.pathname + location.search,
            }),
        );
        navigate(`/templates/${template.id}/edit`);
    }, [template, history, location, dispatch]);

    const isAccessible = useMemo(() => {
        return (
            template &&
            isEntityActionAllowed(TemplateAction.LIST, Entity.TEMPLATE, template?.grantedPermissions) ===
                ActionStatus.ALLOWED
        );
    }, [template]);

    useEffect(() => {
        if (!isAccessible && backButton) {
            // Temporary disable redirect to make sharing templates to work
            // backButton?.onClick();
        }

        if (isAccessible === undefined) {
            resetQueryParams(['template', 'page']);
        }
    }, [isAccessible]);

    return (
        <StyledModalContent>
            {template && (
                <Preview
                    entity={template}
                    createDesign={createDesign}
                    header={
                        <ModalBase.Header
                            title={template?.name || ''}
                            backButton={backButton}
                            additionalActions={
                                template &&
                                template.type === 'TEMPLATE' && (
                                    <>
                                        {!isAccountRoleAssigned(roles.video_brand_studio.video.manage) && (
                                            <TemplateContextMenu
                                                template={template}
                                                menuActions={{
                                                    setDeleteModalTemplate,
                                                    setRenameModalTemplate,
                                                    setMoveModalTemplate,
                                                    setShareModalTemplate,
                                                    duplicateTemplate,
                                                }}
                                                hiddenActions={hiddenMenuActions}
                                                isCardAction={false}
                                            />
                                        )}
                                        {isEntityActionAllowed(
                                            TemplateAction.EDIT,
                                            Entity.TEMPLATE,
                                            template.grantedPermissions,
                                        ) !== ActionStatus.NOT_ALLOWED && (
                                            <Button onClick={editTemplate}>
                                                {translate('pages.categories.category.template.menu.edit')}
                                            </Button>
                                        )}
                                    </>
                                )
                            }
                        />
                    }
                />
            )}
        </StyledModalContent>
    );
};

const NotPreviewableModal = ({
    template,
    isOpen,
    onClose,
    onCloseAnimationEnd,
}: {
    template: Template;
    isOpen: boolean;
    onClose: () => void;
    onCloseAnimationEnd: () => void;
}) => {
    const { translate } = useTranslate();

    const title = useMemo(() => getPreviewErrorTitle(translate, template), [translate, template]);

    const description = useMemo(() => getPreviewErrorDescription(translate, template), [translate, template]);

    return (
        <Modal
            title={title}
            isOpen={isOpen}
            onCloseAnimationEnd={onCloseAnimationEnd}
            onClose={onClose}
            actionPrimary={
                <Button variant="primary" onClick={onClose}>
                    {translate('modal.template.not_published.okay')}
                </Button>
            }
        >
            {description}
        </Modal>
    );
};

const useTemplatePreviewModal = ({
    setDeleteModalTemplate,
    setRenameModalTemplate,
    setMoveModalTemplate,
    setShareModalTemplate,
    createDesign,
    duplicateTemplate,
}: Params) => {
    const [previewModalTemplate, setPreviewModalTemplate] = useState<Template | null>(null);
    const templates: Template[] = useSelector(templatesSelector);
    const previewTemplate = useMemo(
        () => templates.find((t) => t.id === previewModalTemplate?.id),
        [templates, previewModalTemplate?.id],
    );

    const location = useLocation();
    const { resetQueryParams } = useQueryParams();

    // Use refs to avoid stale closures in stable callbacks
    const locationRef = useRef(location);
    locationRef.current = location;
    const resetQueryParamsRef = useRef(resetQueryParams);
    resetQueryParamsRef.current = resetQueryParams;

    const onModalClose = useCallback(() => {
        setPreviewModalTemplate(null);
        resetQueryParamsRef.current(['template', 'page']);

        if (locationRef.current.pathname.includes('categories')) {
            resetQueryParamsRef.current(['create']);
        }
    }, []);

    const isOpen = previewModalTemplate !== null && previewTemplate !== undefined;
    const [shouldRender, onCloseAnimationEnd] = useDeferredClose(isOpen);

    const isPublished = !!previewTemplate?.lastPublishDate;
    const isCorrupted = !!previewTemplate?.corruptionReason;

    const isPreviewable = isPublished && !isCorrupted;

    const renderModalTemplatePreview = () => {
        if (!shouldRender) {
            return null;
        }

        return isPreviewable ? (
            <ModalBase
                container={modalContainer}
                isOpen={isOpen}
                onCloseAnimationEnd={onCloseAnimationEnd}
                onClose={onModalClose}
                size="fullscreen"
            >
                <TemplateModalContent
                    template={previewTemplate}
                    createDesign={() => previewTemplate && createDesign(previewTemplate)}
                    duplicateTemplate={duplicateTemplate}
                    setDeleteModalTemplate={setDeleteModalTemplate}
                    setRenameModalTemplate={setRenameModalTemplate}
                    setMoveModalTemplate={setMoveModalTemplate}
                    setShareModalTemplate={setShareModalTemplate}
                />
            </ModalBase>
        ) : (
            <NotPreviewableModal
                template={previewTemplate}
                isOpen={isOpen}
                onClose={onModalClose}
                onCloseAnimationEnd={onCloseAnimationEnd}
            />
        );
    };

    return {
        previewModalTemplate,
        setPreviewModalTemplate,
        renderModalTemplatePreview,
    };
};

export default useTemplatePreviewModal;

export function getPreviewErrorTitle(translate: (key: string) => string, template: Template | null): string {
    if (!template?.lastPublishDate) {
        return translate('modal.template.not_published.title');
    }

    if (template?.corruptionReason === 'MISSING_ASSET') {
        return translate('modal.template.missing_assets.title');
    }

    // we do not have other corruption reasons yet
    return '';
}

export function getPreviewErrorDescription(translate: (key: string) => string, template: Template | null): string {
    if (!template?.lastPublishDate) {
        return translate('modal.template.not_published.description');
    }

    if (template?.corruptionReason === 'MISSING_ASSET') {
        return translate('modal.template.missing_assets.description');
    }

    // we do not have other corruption reasons yet
    return '';
}
